import { useEffect, useRef } from "react";
import { MapPin, Clock, Phone, Navigation } from "lucide-react";

const hours = [
  { day: "Monday – Friday", time: "9:00 AM – 6:00 PM" },
  { day: "Saturday", time: "9:00 AM – 2:00 PM" },
  { day: "Sunday", time: "Closed" },
];

export default function Location() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="location" data-testid="location-section" className="ez-section bg-white" ref={ref}>
      <div className="ez-container">
        <div className="animate-on-view text-center mb-16">
          <div className="inline-block text-ez-teal font-semibold font-caveat text-lg mb-3">
            Find Us
          </div>
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-ez-text">
            Visit Our <span className="text-ez-teal">Pharmacy</span>
          </h2>
          <p className="text-ez-muted mt-4 max-w-xl mx-auto font-jakarta">
            Conveniently located in East Orange, New Jersey. Easy access, free street parking.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Info Cards */}
          <div className="animate-on-view space-y-4 lg:col-span-1">
            {/* Address */}
            <div className="ez-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin size={22} className="text-ez-teal" />
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-ez-text mb-1">Address</h4>
                  <p className="text-ez-muted text-sm font-jakarta leading-relaxed">
                    447 William St,<br />
                    East Orange, NJ 07017
                  </p>
                  <a
                    href="https://www.google.com/maps/dir//447+William+St,+East+Orange,+NJ+07017"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="get-directions-link"
                    className="inline-flex items-center gap-1 text-ez-teal text-xs font-semibold mt-2 hover:underline"
                  >
                    <Navigation size={12} />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="ez-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone size={22} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-ez-text mb-1">Phone</h4>
                  <a
                    href="tel:+18625201063"
                    data-testid="location-phone-link"
                    className="text-ez-teal font-semibold text-sm hover:underline font-outfit"
                  >
                    (862) 520-1063
                  </a>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="ez-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock size={22} className="text-amber-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-outfit font-bold text-ez-text mb-3">Store Hours</h4>
                  <div className="space-y-2">
                    {hours.map((h) => (
                      <div key={h.day} className="flex justify-between text-sm font-jakarta">
                        <span className="text-ez-muted">{h.day}</span>
                        <span className={`font-semibold ${h.time === "Closed" ? "text-red-500" : "text-ez-text"}`}>
                          {h.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div
            data-testid="map-container"
            className="animate-on-view lg:col-span-2 overflow-hidden rounded-3xl shadow-xl border border-gray-100"
            style={{ transitionDelay: "0.2s" }}
          >
            <iframe
              title="EZ Care Pharmacy Location"
              className="w-full h-[460px]"
              src="https://maps.google.com/maps?q=447%20William%20St%20East%20Orange%20NJ&t=&z=15&ie=UTF8&iwloc=&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
